// War-life decorations (spec-loadout §2): studs earned for cleared waves and
// badges for boss kills, recorded per world. Positions are deterministic from
// `world.runSeed` — identical runs decorate the hull identically. Coordinates
// are hull-local (the hull spans roughly ±0.5 ship units) and snapped toward
// the hull rim: a 16-step ring at radius 0.45.
//
// The renderer (Agent B) reads getShipDecorations(world) every frame; the sim
// never draws. game.ts (parent) calls the recorders on `wave_clear` and
// boss-flagged `death` events.

import type { WorldState } from './state.js';
import { RngStream } from './rng.js';

export type DecorationKind = 'stud' | 'badge-maw' | 'badge-razor';

export interface Decoration {
  kind: DecorationKind;
  /** Hull-local coordinates in ship units, inside ±0.5, snapped to the rim. */
  x: number;
  y: number;
}

/** One 'stud' per this many cleared waves (spec-loadout §2). */
export const WAVES_PER_STUD = 5;
/** Cap on wave-clear studs. */
export const MAX_STUDS = 6;
/** Cap on total boss badges. */
export const MAX_BADGES = 4;

/** Boss def id → badge kind. Other defs earn nothing. */
const BOSS_BADGES: Readonly<Record<string, DecorationKind>> = {
  'enemy.abyss-maw': 'badge-maw',
  'enemy.void-razor': 'badge-razor'
};

// Rim snap: decorations sit on a 16-step ring just inside the ±0.5 hull.
const RIM_STEPS = 16;
const RIM_RADIUS = 0.45;

interface DecorationState {
  seed: number;
  /** Tick of the last recorder call; a tick rewind means a same-seed restart. */
  lastTick: number;
  decorations: Decoration[];
  studs: number;
  badges: number;
}

// Seed-guarded per-world record; the WeakMap drops state with recycled worlds.
const stateMap = new WeakMap<WorldState, DecorationState>();

/**
 * Per-world decoration state. Reset guards: a run-seed change (new run) or a
 * tick rewind (same-seed restart, where world.tick returns to 0) starts the
 * record over, so decorations never leak across runs.
 */
function getState(world: WorldState): DecorationState {
  let state = stateMap.get(world);
  if (!state || state.seed !== world.runSeed || world.tick < state.lastTick) {
    state = {
      seed: world.runSeed,
      lastTick: world.tick,
      decorations: [],
      studs: 0,
      badges: 0
    };
    stateMap.set(world, state);
  }
  return state;
}

/**
 * Deterministic rim position for the decoration at `index`: mulberry32
 * (seed ^ index) picks one of 16 angular steps on the hull rim.
 */
function rimPosition(seed: number, index: number): { x: number; y: number } {
  const rng = new RngStream((seed ^ index) >>> 0);
  const angle = (rng.next_int(RIM_STEPS) / RIM_STEPS) * Math.PI * 2;
  return { x: Math.cos(angle) * RIM_RADIUS, y: Math.sin(angle) * RIM_RADIUS };
}

/**
 * Record a cleared wave (the director's `wave_clear` wave number): one 'stud'
 * per 5 cleared waves, cap 6. Idempotent for repeated/out-of-order reports of
 * the same wave count — studs track floor(wave / 5), never double-counted.
 */
export function recordWaveClear(world: WorldState, wave: number): void {
  const state = getState(world);
  state.lastTick = Math.max(state.lastTick, world.tick);
  const target = Math.min(MAX_STUDS, Math.floor(wave / WAVES_PER_STUD));
  while (state.studs < target) {
    const pos = rimPosition(world.runSeed, state.decorations.length);
    state.decorations.push({ kind: 'stud', x: pos.x, y: pos.y });
    state.studs++;
  }
}

/**
 * Record a boss kill by enemy def id: 'enemy.abyss-maw' → 'badge-maw',
 * 'enemy.void-razor' → 'badge-razor'. Cap 4 badges total; unknown defs earn
 * nothing.
 */
export function recordBossKill(world: WorldState, defId: string): void {
  const state = getState(world);
  state.lastTick = Math.max(state.lastTick, world.tick);
  const kind = BOSS_BADGES[defId];
  if (!kind || state.badges >= MAX_BADGES) return;
  const pos = rimPosition(world.runSeed, state.decorations.length);
  state.decorations.push({ kind, x: pos.x, y: pos.y });
  state.badges++;
}

/**
 * Current hull decorations. Returns a shallow copy, so callers (the render
 * decal layer) can hold it across frames without aliasing sim state.
 */
export function getShipDecorations(world: WorldState): Decoration[] {
  return getState(world).decorations.slice();
}
